import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useListAllCourtsQuery } from '@/store/courtsApi';
import { useListSprawaStatusDefinitionsQuery } from '@/store/sprawyApi';
import { SprawaPortalStatus as SprawaPortalStatusEnum } from '@/types';
import type { CreateSprawaRequest, SprawaDto, SprawaPortalStatus } from '@/types';
import { includesIgnoringDiacritics } from '@/utils/normalize';
import {
  CourtFieldWrapper,
  CourtSuggestionsList,
  CourtSuggestionItem,
} from './SprawaFormModal.styled';

interface SprawaFormModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit: (data: CreateSprawaRequest) => Promise<void> | void;
  editing?: SprawaDto | null;
}

interface FormValues {
  signature: string;
  court: string;
  department: string;
  judge: string;
  subject: string;
  repertoryType: string;
  receiptDate: string;
  finishDate: string;
  value: string;
  portalStatus: SprawaPortalStatus;
  statusDefinitionId: string;
}

const EMPTY: FormValues = {
  signature: '',
  court: '',
  department: '',
  judge: '',
  subject: '',
  repertoryType: '',
  receiptDate: '',
  finishDate: '',
  value: '',
  portalStatus: SprawaPortalStatusEnum.WToku,
  statusDefinitionId: '',
};

const stripEmpty = (v: FormValues): CreateSprawaRequest => ({
  signature: v.signature || undefined,
  court: v.court || undefined,
  department: v.department || undefined,
  judge: v.judge || undefined,
  subject: v.subject || undefined,
  repertoryType: v.repertoryType || undefined,
  receiptDate: v.receiptDate || undefined,
  finishDate: v.finishDate || undefined,
  value: v.value || undefined,
  portalStatus: v.portalStatus,
  statusDefinitionId: v.statusDefinitionId || undefined,
});

export default function SprawaFormModal({ show, onClose, onSubmit, editing }: SprawaFormModalProps) {
  const { t } = useTranslation();
  const { data: courts } = useListAllCourtsQuery();
  const { data: statusDefinitions } = useListSprawaStatusDefinitionsQuery();

  const { register, handleSubmit, reset, control, watch, setValue, formState: { isSubmitting } } = useForm<FormValues>({
    defaultValues: EMPTY,
  });

  const [courtOpen, setCourtOpen] = useState(false);
  const courtRef = useRef<HTMLDivElement>(null);
  const courtValue = watch('court');

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (courtRef.current && !courtRef.current.contains(e.target as Node)) setCourtOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!show) return;
    if (editing) {
      reset({
        signature: editing.signature ?? '',
        court: editing.court ?? '',
        department: editing.department ?? '',
        judge: editing.judge ?? '',
        subject: editing.subject ?? '',
        repertoryType: editing.repertoryType ?? '',
        receiptDate: editing.receiptDate ?? '',
        finishDate: editing.finishDate ?? '',
        value: editing.value ?? '',
        portalStatus: editing.portalStatus,
        statusDefinitionId: editing.statusDefinitionId ?? '',
      });
    } else {
      reset(EMPTY);
    }
  }, [show, editing, reset]);

  const courtSuggestions = useMemo(() => {
    if (!courts) return [];
    if (!courtValue) return courts;
    return courts.filter((c) => includesIgnoringDiacritics(c.name, courtValue));
  }, [courts, courtValue]);

  const submit = handleSubmit(async (values) => {
    await onSubmit(stripEmpty(values));
    onClose();
  });

  return (
    <Modal show={show} onHide={onClose} size="lg">
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title>{editing ? t('sprawy.editSprawa') : t('sprawy.addSprawa')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>{t('sprawy.form.signature')}</Form.Label>
            <Form.Control {...register('signature')} />
          </Form.Group>
          <div className="row">
            <Form.Group className="mb-3 col-6">
              <CourtFieldWrapper ref={courtRef}>
                <Form.Label>{t('sprawy.form.court')}</Form.Label>
                <Controller
                  control={control}
                  name="court"
                  render={({ field }) => (
                    <Form.Control
                      autoComplete="off"
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        setCourtOpen(true);
                      }}
                      onFocus={() => setCourtOpen(true)}
                    />
                  )}
                />
                {courtOpen && courtSuggestions.length > 0 && (
                  <CourtSuggestionsList>
                    {courtSuggestions.map((c) => (
                      <CourtSuggestionItem
                        key={c.id}
                        onMouseDown={() => {
                          setValue('court', c.name, { shouldDirty: true });
                          setCourtOpen(false);
                        }}
                      >
                        {c.name}
                      </CourtSuggestionItem>
                    ))}
                  </CourtSuggestionsList>
                )}
              </CourtFieldWrapper>
            </Form.Group>
            <Form.Group className="mb-3 col-6">
              <Form.Label>{t('sprawy.form.department')}</Form.Label>
              <Form.Control {...register('department')} />
            </Form.Group>
          </div>
          <div className="row">
            <Form.Group className={`mb-3 ${editing ? 'col-4' : 'col-6'}`}>
              <Form.Label>{t('sprawy.form.judge')}</Form.Label>
              <Form.Control {...register('judge')} />
            </Form.Group>
            {editing && (
              <Form.Group className="mb-3 col-4">
                <Form.Label>{t('sprawy.form.portalStatus')}</Form.Label>
                <Form.Select {...register('portalStatus')}>
                  {Object.values(SprawaPortalStatusEnum).map((s) => (
                    <option key={s} value={s}>{t(`sprawaStatus.${s}`)}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}
            <Form.Group className={`mb-3 ${editing ? 'col-4' : 'col-6'}`}>
              <Form.Label>{t('sprawy.form.myStatus')}</Form.Label>
              <Form.Select {...register('statusDefinitionId')}>
                <option value="">{'—'}</option>
                {statusDefinitions?.map((sd) => (
                  <option key={sd.id} value={sd.id}>{sd.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>
          <Form.Group className="mb-3">
            <Form.Label>{t('sprawy.form.subject')}</Form.Label>
            <Form.Control {...register('subject')} />
          </Form.Group>
          <div className="row">
            <Form.Group className="mb-3 col-4">
              <Form.Label>{t('sprawy.form.repertoryType')}</Form.Label>
              <Form.Control {...register('repertoryType')} />
            </Form.Group>
            <Form.Group className="mb-3 col-4">
              <Form.Label>{t('sprawy.form.receiptDate')}</Form.Label>
              <Form.Control type="date" {...register('receiptDate')} />
            </Form.Group>
            <Form.Group className="mb-3 col-4">
              <Form.Label>{t('sprawy.form.finishDate')}</Form.Label>
              <Form.Control type="date" {...register('finishDate')} />
            </Form.Group>
          </div>
          <Form.Group className="mb-3">
            <Form.Label>{t('sprawy.form.value')}</Form.Label>
            <Form.Control {...register('value')} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>{t('confirm.cancel')}</Button>
          <Button type="submit" variant="primary" disabled={isSubmitting}>
            {editing ? t('confirm.confirm') : t('sprawy.addSprawa')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
